---
title: Repositories
lede: One project per git repository, or per folder of a monorepo: how it installs, runs and is checked, and what it is given.
order: 6
part: Get started
---

Each repository of a workspace is a **project**, and so is each folder of a monorepo that runs on its own. You configure them in **Workspace settings › Repositories**.

![The repositories panel](media/ws-repositories.webp)

The button at the top picks the project to edit; its menu also holds **Add folders** and **Remove**. [**Complete with Agent**](/agent-panel), above it, asks the agent to fill in what detection missed. In a micro-VM, each repository is cloned inside the sandbox. Nothing is mounted: the sandbox has no path back to your disk.

### Project settings

Airlock fills these in when you add a repository. Every field stays editable.

| Field | What it decides |
|---|---|
| Runtime | What the session image installs: Node.js, Python, Java, Go, Rust, PHP, Static or Other |
| Package manager | npm, pnpm, yarn or bun. Node.js only |
| What it serves | **Interface** (Preview opens a browser on the port), **API** (the API console), both, or neither |
| Default branch | What a review is diffed from: `main`, `master`, `trunk` |
| Install command | Runs once when the session starts — skipped when the dependencies the session restored are exactly what the build files describe |
| Skip when dependencies are unchanged | On by default. Turn it off for an install that also builds or generates files, so it always runs |
| Run command | The process the dock starts and shows the logs of |
| Port | What Preview opens |
| Port variable | The variable a second copy of the project reads to bind elsewhere, when it runs on this computer. `PORT` is always set |
| Health check | A command polled after start; the dock shows *starting* until it exits 0 |
| Check command | What proves the project still works. A workflow step can run it |

If the workspace publishes ports through [tunnels](/tunnels), two more rows appear: **Tunnel domain**, a domain reserved with your provider so the public URL stays the same, and **Serve through the tunnel**: **Opened on start** or **Opened by hand**.

### Monorepos

A folder of a repository can be a project of its own, such as `apps/web`. When you add a repository, Airlock proposes the folders it finds as projects; remove the ones that are not.

- **The repository's top** has no **What it serves**, run command or port. Its **Install command** is the install for the whole repository, and its **Check command** covers it beyond each folder's own.
- **Each folder** has its own run command, port and environment. Its **Folder** field, such as `apps/web`, replaces the default branch: a folder is cloned, reviewed and merged with its repository.
- **Removing the repository** removes its folders with it.

In the task form, a monorepo's folders are listed under their repository, each ticked separately.

In a [shared workspace](/organization), folders travel with it. A member who joins clones the repository once, and its folders come with it.

### Environment variables

A project's variables and its environment file are set on Local, in **Workspace settings › [Targets](/targets)**. The row **Variables** here says how many it has, and **Edit in Targets** opens them.

### Leaving a project out of a task

A repository unticked in the task form is not cloned, installed or reviewed in that session. This keeps a large workspace fast for a change that touches one repository.
